import React, { useState } from 'react';
import { toggleLike, deletePost } from '../services/postService';
import ReplyForm from './ReplyForm';
import ReplyList from './ReplyList';
import EditPostForm from './EditPostForm';
import MentionFormatter from './MentionFormatter';
import './Post.css';

const Post = ({ post, currentUser }) => {
  const [isLiking, setIsLiking] = useState(false);
  const [showLikedBy, setShowLikedBy] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const handleLike = async () => {
    if (isLiking) return;
    
    setIsLiking(true);
    try {
      await toggleLike(post.id, currentUser);
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deletePost(post.id, currentUser.uid);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const likedByUsers = post.likedByUsers || [];
  const isLikedByCurrentUser = likedByUsers.some(user => user.id === currentUser.uid);
  const likesCount = post.likesCount || 0;

  const getUsername = (email) => {
    return email.split('@')[0];
  };

  const getLikedByUsers = () => {
    return likedByUsers.map(user => getUsername(user.email));
  };

  return (
    <div id={`post-${post.id}`} className="post">
      <div className="post-header">
        <div className="post-author">
          <div className="author-avatar">
            {post.authorEmail?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="author-info">
            <div className="author-email">{post.authorEmail}</div>
            <div className="post-time">{formatDate(post.createdAt)}</div>
          </div>
        </div>
        
        {post.authorId === currentUser.uid && (
          <div className="post-actions-menu">
            <button 
              className="edit-post-button"
              onClick={() => setIsEditing(true)}
              disabled={isDeleting || isEditing}
              title="Edit post"
            >
              ✏️
            </button>
            <button 
              className="delete-post-button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting || isEditing}
              title="Delete post"
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      <div className="post-content">
        {isEditing ? (
          <EditPostForm 
            post={post}
            currentUser={currentUser}
            onEditComplete={() => setIsEditing(false)}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <div>
            <p><MentionFormatter text={post.text} /></p>
            {post.isEdited && (
              <div className="edited-indicator">
                <span>✏️ Edited {post.editedAt ? formatDate(post.editedAt) : ''}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="post-actions">
        <div className="like-section">
          <button 
            className={`like-button ${isLikedByCurrentUser ? 'liked' : ''}`}
            onClick={handleLike}
            disabled={isLiking}
            onMouseEnter={() => setShowLikedBy(true)}
            onMouseLeave={() => setShowLikedBy(false)}
          >
            <span className="like-icon">
              {isLikedByCurrentUser ? '👍' : '👍'}
            </span>
            <span className="like-count">{likesCount}</span>
          </button>
          
          {showLikedBy && likedByUsers.length > 0 && (
            <div className="likes-tooltip">
              <div className="likes-tooltip-header">
                Liked by {likesCount} {likesCount === 1 ? 'person' : 'people'}
              </div>
              <div className="likes-tooltip-users">
                {getLikedByUsers().map((username, index) => (
                  <div key={index} className="likes-tooltip-user">
                    {username}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {showLikedBy && likedByUsers.length === 0 && (
            <div className="likes-tooltip">
              <div className="likes-tooltip-header">No likes yet</div>
            </div>
          )}
        </div>

        <div className="reply-section">
          <button 
            className="reply-toggle-button"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            💬 Reply
          </button>
          
          {(post.repliesCount || 0) > 0 && (
            <button 
              className="replies-view-button"
              onClick={() => setShowReplies(!showReplies)}
            >
              {showReplies ? 'Hide' : 'View'} {post.repliesCount || 0} {(post.repliesCount || 0) === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      </div>

      {showReplyForm && (
        <ReplyForm 
          postId={post.id} 
          user={currentUser}
          channelId={post.channelId}
          onReplyCreated={() => {
            setShowReplies(true);
            setShowReplyForm(false);
          }} 
        />
      )}

      {showReplies && (
        <ReplyList postId={post.id} currentUser={currentUser} />
      )}

      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Delete Post</h3>
            <p>Are you sure you want to delete this post? This will also delete all replies and likes. This action cannot be undone.</p>
            <div className="delete-confirmation-actions">
              <button 
                className="delete-confirm-button"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button 
                className="delete-cancel-button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Post;